import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
  EBackgroundOperationStatus,
  IBackgroundOperation,
  IStartCoiGenerationParams,
} from "@app/core/background-operations/background-operation.type";
import { TDocumentStatusChanged } from "@app/core/signalR/browser-notify.type";
import { NotificationHubService } from "@app/core/signalR/notification-hub.service";
import { EDocumentGenerationStatus } from "@app/shared/types/policy-document.type";

@Injectable({ providedIn: "root" })
export class BackgroundOperationService {
  private readonly operationsState = signal<readonly IBackgroundOperation[]>([]);

  readonly operations = this.operationsState.asReadonly();
  readonly hasOperations = computed(() => this.operationsState().length > 0);

  private readonly notificationHubService = inject(NotificationHubService);

  constructor() {
    this.notificationHubService.documentStatusChanged$
      .pipe(takeUntilDestroyed())
      .subscribe(change => this.onDocumentStatusChanged(change));
  }

  startCoiGeneration(params: IStartCoiGenerationParams): void {
    const operation: IBackgroundOperation = {
      id: crypto.randomUUID(),
      ...params,
      status: EBackgroundOperationStatus.InProgress,
    };

    this.operationsState.update(operations => [
      ...operations.filter(item => item.policyId !== params.policyId),
      operation,
    ]);
  }

  dismiss(operationId: string): void {
    this.operationsState.update(operations => operations.filter(item => item.id !== operationId));
  }

  private onDocumentStatusChanged(change: TDocumentStatusChanged): void {
    const status = this.toOperationStatus(change.status);

    if (!status || !change.policyId) {
      return;
    }

    this.operationsState.update(operations =>
      operations.map(operation =>
        operation.policyId === change.policyId && operation.status === EBackgroundOperationStatus.InProgress
          ? { ...operation, status }
          : operation,
      ),
    );
  }

  private toOperationStatus(status: EDocumentGenerationStatus): EBackgroundOperationStatus | null {
    switch (status) {
      case EDocumentGenerationStatus.Completed:
        return EBackgroundOperationStatus.Completed;
      case EDocumentGenerationStatus.Failed:
        return EBackgroundOperationStatus.Failed;
      default:
        return null;
    }
  }
}
